import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { AppBar, makeStyles } from '@material-ui/core'
import QuestionMarkIcon from 'components/RightPane/QuestionMarkIcon'
import useBetaWorkspaceToggle from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/hooks/useBetaWorkspaceToggle'
import React, { RefObject, createRef, useRef } from 'react'
import ChatDisclaimer from './ChatDisclaimer'
import ChatToolbar from './ChatToolbar'
import MessageContainer from './MessageContainer'
import MessagePrompt from './MessagePrompt'
import { chatHeaderColorBeta, chatPrimaryColor } from './colors'
import { useChatBot } from './useChatBotHook'

export interface AskDelaChatComponentStylesProps {
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: pxToRem(5),
    },
    header: (props: AskDelaChatComponentStylesProps) => ({
        fontFamily: 'Roboto',
        fontSize: pxToRem(16),
        fontWeight: 500,
        backgroundColor: props.isBetaWorkspaceToggleEnabled
            ? chatHeaderColorBeta
            : chatPrimaryColor,
        flex: '0 0 auto',
        ...(props.isBetaWorkspaceToggleEnabled && {
            borderBottom: '1px solid rgba(80, 67, 67, 0.1)',
            boxShadow: 'none !important',
        }),
    }),
    content: (props: AskDelaChatComponentStylesProps) => ({
        display: 'flex',
        flex: '1 1 auto',
        // column - messages start from top.
        // column-reverse - messages start from bottom
        flexDirection: 'column',
        overflowY: 'auto',
        padding: theme.spacing(1),
        fontFamily: props.isBetaWorkspaceToggleEnabled ? 'Lexend' : 'initial',
        backgroundColor: props.isBetaWorkspaceToggleEnabled
            ? chatHeaderColorBeta
            : theme.palette.common.white,
    }),
    footer: {
        width: '100%',
        display: 'flex',
        flex: '0 0 auto',
    },
    questionMarkIcon: {
        top: pxToRem(2),
        left: pxToRem(2),
        display: 'flex',
        marginLeft: 'auto',
    },
}))

export interface AskDelaChatComponentProps {
    isOnHeader?: boolean
}

const AskDelaChatComponent = ({
    isOnHeader = false,
}: AskDelaChatComponentProps) => {
    const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
    const classes = useStyles({ isBetaWorkspaceToggleEnabled })
    const { messages, isDisclaimerOpen } = useChatBot()

    //#region Accessibility

    // Used to determine when scrolling to bottom of the page
    const [messageCount, setMessageCount] = React.useState<number>(0)

    // Used to track the focused message index - start at the latest message
    const [focusedMessageIndex, setFocusedMessageIndex] = React.useState<
        number
    >(messages.length - 1)

    /** Used to scroll to the bottom of the conversation when the user asks or the agent answers */
    const messagesEndRef: RefObject<HTMLDivElement> = React.useRef(null)

    const scrollToBottom = React.useCallback(() => {
        messagesEndRef?.current?.scrollIntoView?.({ behavior: 'smooth' })
    }, [])

    /** Handle upward keyboard navigation on the messages in the chat */
    const navigateMessagesUp = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            event.stopPropagation()
            event.preventDefault()

            const newFocusedMessageIndex = Math.max(focusedMessageIndex - 1, 0)

            // focus on the previous message
            messageRefs.current[newFocusedMessageIndex]?.current?.focus()
            setFocusedMessageIndex(newFocusedMessageIndex)
        },
        [focusedMessageIndex]
    )

    /** Handle downward keyboard navigation on the messages in the chat */
    const navigateMessageDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            event.stopPropagation()
            event.preventDefault()

            const newFocusedMessageIndex = Math.min(
                focusedMessageIndex + 1,
                messages.length - 1
            )

            // focus on the next message
            messageRefs.current[newFocusedMessageIndex]?.current?.focus()
            setFocusedMessageIndex(newFocusedMessageIndex)
        },
        [focusedMessageIndex, messages]
    )

    const handleContentKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key === 'ArrowUp') {
                navigateMessagesUp(event)
            }

            if (event.key === 'ArrowDown') {
                navigateMessageDown(event)
            }
        },
        [navigateMessagesUp, navigateMessageDown]
    )

    /** Used to know which message is currently being navigated via keyboard */
    const getMessageTabIndex = React.useCallback(
        (index: number) => {
            return index === focusedMessageIndex ? 0 : -1
        },
        [focusedMessageIndex]
    )

    /** Reference to individual message containers used for focusing during keyboard navigation */
    const messageRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([])
    React.useEffect(() => {
        messageRefs.current = Array(messages.length)
            .fill(null)
            .map((_, index) => messageRefs.current[index] || createRef())
    }, [messages])

    React.useEffect(() => {
        // Scroll to the bottom of the conversation when a new message is added
        if (messages.length !== messageCount) {
            setMessageCount(messages.length)
            scrollToBottom()
        }
    }, [messages])

    //#endregion

    const firstSuggestedIndex = React.useMemo(
        () => messages.findIndex((msg) => msg.role === 'suggested'),
        [messages]
    )

    return (
        <div className={classes.root} data-testid="askDelaChat">
            <AppBar
                className={classes.header}
                position="sticky"
                data-testid="appBar"
            >
                <ChatToolbar isOnHeader={isOnHeader} />
            </AppBar>

            {isDisclaimerOpen ? (
                <ChatDisclaimer />
            ) : (
                <>
                    <div
                        className={classes.content}
                        data-testid="content"
                        onKeyDown={handleContentKeyDown}
                    >
                        {isBetaWorkspaceToggleEnabled && (
                            <div className={classes.questionMarkIcon}>
                                <QuestionMarkIcon />
                            </div>
                        )}
                        <div>
                            {messages.map((message, index) => (
                                <MessageContainer
                                    key={message.id}
                                    message={message}
                                    tabIndex={getMessageTabIndex(index)}
                                    ref={messageRefs.current[index]}
                                    isOnHeader={isOnHeader}
                                    isFirstSuggested={
                                        index === firstSuggestedIndex
                                    }
                                />
                            ))}
                            <div ref={messagesEndRef}></div>
                        </div>
                    </div>
                    <div className={classes.footer} data-testid="prompt">
                        <MessagePrompt />
                    </div>
                </>
            )}
        </div>
    )
}

export default AskDelaChatComponent
